import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { NudgeAlert } from '../components/NudgeAlert';
import { useApp } from '../context/AppContext';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import {
  adminAdjustCredits,
  adminApplyAppSumoLicense,
  adminLookupAppSumoLicense,
  adminDeleteAccount,
  adminGetAutomationDiagnostics,
  adminListAccounts,
  adminResetMailbox,
  adminRunTesterCycle,
  adminSetAccountStatus,
  adminSetTesterMode,
  adminUpdateSubscription,
} from '../services/appStateService';
import { NUDGE_PLANS, formatLimit } from '../lib/nudgePlanLimits';

const PLAN_IDS = ['free', 'early_access', 'legacy_pro', 'pro_ph_trial', 'pro_2026', 'agency_6', 'agency_15', 'agency_25', 'agency_50', 'appsumo_tier_1', 'appsumo_tier_2', 'appsumo_tier_3', 'appsumo_tier_4', 'appsumo_tier_5'];

function planLabel(planId) {
  return NUDGE_PLANS[planId]?.label || planId;
}


function formatAdminDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function humanizeReason(value = '') {
  const clean = String(value || '').trim();
  if (!clean) return '';
  const labels = {
    auto_drafts_disabled: 'Automatic drafts are off',
    auto_send_approval_required: 'Waiting for conversation approval',
    recipient_not_on_allowlist: 'Recipient is not approved for auto-send',
    mailbox_reconnect_required: 'Mailbox needs reconnect',
    mailbox_disconnected: 'Mailbox disconnected',
    cross_thread_reply_resolved: 'A newer reply resolved the follow-up',
    cross_thread_reply_needs_review: 'A newer email needs review',
    automation_already_processing: 'Already being processed',
    out_of_credits: 'Out of credits',
  };
  return labels[clean] || clean.replace(/_/g, ' ');
}

function AutomationResult({ result }) {
  if (!result) return null;
  const scan = result.scan || {};
  const automation = result.automation || {};

  return (
    <Box sx={{ mt: 1.2, p: 1.35, border: '1px solid rgba(26,29,49,.10)', borderRadius: 1.2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
        <Typography variant="body2">
          <strong>Scan:</strong> {scan.scanned || 0} scanned · {scan.found || 0} found · {scan.saved || 0} saved
        </Typography>
        <Typography variant="body2">
          <strong>Automation:</strong> {automation.drafted || 0} drafted · {automation.sent || 0} sent · {automation.skipped || 0} skipped · {automation.failed || 0} failed
        </Typography>
        {automation.nextDueAt && (
          <Typography variant="body2"><strong>Next due:</strong> {formatAdminDate(automation.nextDueAt)}</Typography>
        )}
      </Stack>

      {(result.warnings || []).map((warning) => (
        <Typography key={warning} variant="caption" color="warning.main" display="block" sx={{ mt: .65 }}>
          {warning}
        </Typography>
      ))}

      {Object.keys(automation.skippedByReason || {}).length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .65 }}>
          Skipped: {Object.entries(automation.skippedByReason).map(([reason, count]) => `${humanizeReason(reason)} (${count})`).join(' · ')}
        </Typography>
      )}
    </Box>
  );
}

function AccountRow({ account, onChanged }) {
  const normalizedAdminPlanId = ['pro', 'paid', 'stripe_pro'].includes(String(account.planId || '').toLowerCase())
    ? 'legacy_pro'
    : (account.planId || 'free');
  const [planId, setPlanId] = useState(normalizedAdminPlanId);
  const [status, setStatus] = useState(account.status || 'inactive');
  const [credits, setCredits] = useState('100');
  const [busy, setBusy] = useState('');
  const [testerEnabled, setTesterEnabled] = useState(Boolean(account.testerMode?.enabled));
  const [minutesPerDay, setMinutesPerDay] = useState(String(account.testerMode?.minutesPerDay || 1));
  const [diagnostics, setDiagnostics] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [appSumoLicenseKey, setAppSumoLicenseKey] = useState(account.appsumo?.licenseKey || '');
  const [appSumoLookup, setAppSumoLookup] = useState(null);
  const [appSumoError, setAppSumoError] = useState('');

  useEffect(() => {
    setTesterEnabled(Boolean(account.testerMode?.enabled));
    setMinutesPerDay(String(account.testerMode?.minutesPerDay || 1));
  }, [account.testerMode?.enabled, account.testerMode?.minutesPerDay]);

  const savePlan = async () => {
    setBusy('plan');
    try {
      await adminUpdateSubscription({ accountId: account.accountId, planId, status: account.subscriptionStatus || 'active', source: 'manual' });
      await onChanged?.('Plan updated.');
    } finally {
      setBusy('');
    }
  };

  const grantAppSumoTier5 = async () => {
    setBusy('appsumo_t5');
    try {
      await adminUpdateSubscription({
        accountId: account.accountId,
        planId: 'appsumo_tier_5',
        status: 'active',
        source: 'appsumo_qa_manual',
      });
      setPlanId('appsumo_tier_5');
      await onChanged?.('AppSumo Tier 5 test access granted.');
    } finally {
      setBusy('');
    }
  };

  const lookupAppSumoLicense = async () => {
    const licenseKey = String(appSumoLicenseKey || '').trim();
    if (!licenseKey) return;
    setBusy('appsumo_lookup');
    setAppSumoError('');
    try {
      const result = await adminLookupAppSumoLicense({ licenseKey });
      setAppSumoLookup(result.license || null);
    } catch (err) {
      setAppSumoLookup(null);
      setAppSumoError(err?.message || 'Could not verify this AppSumo license.');
    } finally {
      setBusy('');
    }
  };

  const applyAppSumoLicense = async () => {
    const licenseKey = String(appSumoLicenseKey || '').trim();
    if (!licenseKey) return;
    setBusy('appsumo_apply');
    setAppSumoError('');
    try {
      const result = await adminApplyAppSumoLicense({
        accountId: account.accountId,
        licenseKey,
      });
      setPlanId(result?.license?.planId || planId);
      setAppSumoLookup(result?.license || null);
      await onChanged?.(`AppSumo Tier ${result?.license?.tier || ''} license applied.`);
    } catch (err) {
      setAppSumoError(err?.message || 'Could not apply this AppSumo license.');
    } finally {
      setBusy('');
    }
  };

  const saveStatus = async () => {
    setBusy('status');
    try {
      await adminSetAccountStatus({ accountId: account.accountId, status });
      await onChanged?.('Account status updated.');
    } finally {
      setBusy('');
    }
  };

  const addCredits = async () => {
    const delta = Number(credits || 0);
    if (!delta) return;
    setBusy('credits');
    try {
      await adminAdjustCredits({ accountId: account.accountId, delta, reason: 'admin_top_up' });
      await onChanged?.('Credits adjusted.');
    } finally {
      setBusy('');
    }
  };

  const resetMailbox = async (mailboxId) => {
    setBusy(mailboxId);
    try {
      await adminResetMailbox({ mailboxId });
      await onChanged?.('Mailbox reset.');
    } finally {
      setBusy('');
    }
  };


  const toggleTesterMode = async () => {
    const nextEnabled = !testerEnabled;
    setBusy('tester_mode');
    try {
      await adminSetTesterMode({
        accountId: account.accountId,
        enabled: nextEnabled,
        minutesPerDay: Number(minutesPerDay || 1),
      });
      setTesterEnabled(nextEnabled);
      setTestResult(null);
      setDiagnostics(null);
      await onChanged?.(nextEnabled ? 'Tester mode enabled.' : 'Tester mode disabled.');
    } finally {
      setBusy('');
    }
  };

  const saveTesterSpeed = async () => {
    if (!testerEnabled) return;
    setBusy('tester_speed');
    try {
      await adminSetTesterMode({
        accountId: account.accountId,
        enabled: true,
        minutesPerDay: Number(minutesPerDay || 1),
      });
      await onChanged?.('Tester timing updated.');
    } finally {
      setBusy('');
    }
  };

  const loadDiagnostics = async () => {
    setBusy('diagnostics');
    try {
      const result = await adminGetAutomationDiagnostics({ accountId: account.accountId });
      setDiagnostics(result);
      setShowDiagnostics(true);
    } finally {
      setBusy('');
    }
  };

  const runTestNow = async () => {
    setBusy('run_test');
    try {
      const result = await adminRunTesterCycle({ accountId: account.accountId });
      setTestResult(result);
      const fresh = await adminGetAutomationDiagnostics({ accountId: account.accountId });
      setDiagnostics(fresh);
      setShowDiagnostics(true);
      await onChanged?.('Tester cycle completed.');
    } finally {
      setBusy('');
    }
  };

  const deleteAccount = async () => {
    const expected = account.email || account.accountId;

    const typed = window.prompt(
      `Type ${expected} to delete this NudgeForMe account data. This resets welcome-email testing.`
    );

    if (!typed || typed.trim().toLowerCase() !== String(expected).trim().toLowerCase()) {
      return;
    }

    const authTyped = window.prompt(
      'Optional: type DELETE AUTH to also delete the Firebase Auth user. Leave blank to keep Auth and only delete Nudge data.'
    );

    const deleteAuthUser = authTyped === 'DELETE AUTH';

    setBusy('delete');

    try {
      const result = await adminDeleteAccount({
        accountId: account.accountId,
        email: account.email,
        confirm: typed,
        deleteAuthUser,
      });

      await onChanged?.(
        deleteAuthUser
          ? `Account and Auth user deleted. Removed ${result.deletedDocs || 0} records.`
          : `Nudge account data deleted. Removed ${result.deletedDocs || 0} records.`
      );
    } finally {
      setBusy('');
    }
  };

  const limits = account.limits || {};
  return (
    <Card sx={{ borderRadius: 1.25 }}>
      <CardContent sx={{ p: 2.4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} alignItems={{ md: 'flex-start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" sx={{ fontSize: 20 }} noWrap>{account.email || account.accountId}</Typography>
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={account.status || 'inactive'} sx={{ borderRadius: 1 }} />
              <Chip size="small" label={planLabel(account.planId)} sx={{ borderRadius: 1 }} />
              <Chip size="small" label={`${account.mailboxes?.length || 0} inbox${account.mailboxes?.length === 1 ? '' : 'es'}`} variant="outlined" sx={{ borderRadius: 1 }} />
            </Stack>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Select size="small" value={planId} onChange={(event) => setPlanId(event.target.value)} sx={{ minWidth: 170 }}>
              {PLAN_IDS.map((id) => <MenuItem key={id} value={id}>{planLabel(id)}</MenuItem>)}
            </Select>
            <Button variant="outlined" onClick={savePlan} disabled={busy === 'plan'}>Set plan</Button>
            <Button
              variant="contained"
              onClick={grantAppSumoTier5}
              disabled={Boolean(busy) || account.planId === 'appsumo_tier_5'}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {busy === 'appsumo_t5' ? 'Granting…' : 'Grant T5 test'}
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2} mt={2.2}>
          <Box>
            <Typography variant="caption" color="text.secondary">Credits</Typography>
            <Typography fontWeight={800}>{formatLimit(account.creditsUsed)} / {formatLimit(limits.credits || limits.followUpCreditsMonthly)}</Typography>
            <Typography variant="caption" color="text.secondary">Bonus: {formatLimit(account.bonusCredits)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Opportunity scans</Typography>
            <Typography fontWeight={800}>{formatLimit(account.opportunityScansUsed)} / {formatLimit(limits.opportunityScansMonthly)}</Typography>
            <Typography variant="caption" color="text.secondary">Found {formatLimit(account.opportunitiesFound)} · filtered {formatLimit(account.opportunitiesRejected)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Drafts / replies</Typography>
            <Typography fontWeight={800}>{formatLimit(account.followUpsDrafted)} drafted · {formatLimit(account.repliesReceived)} replies</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography fontWeight={900}>AppSumo license</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .35 }}>
            For real AppSumo customers, paste the license key here. The backend verifies the actual tier with AppSumo before applying it.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mt: 1.2 }}>
            <TextField
              size="small"
              label="AppSumo license key"
              value={appSumoLicenseKey}
              onChange={(event) => {
                setAppSumoLicenseKey(event.target.value);
                setAppSumoLookup(null);
                setAppSumoError('');
              }}
              sx={{ minWidth: { md: 360 } }}
            />
            <Button variant="outlined" onClick={lookupAppSumoLicense} disabled={!appSumoLicenseKey.trim() || Boolean(busy)}>
              {busy === 'appsumo_lookup' ? 'Checking…' : 'Check license'}
            </Button>
            <Button variant="contained" onClick={applyAppSumoLicense} disabled={!appSumoLicenseKey.trim() || Boolean(busy)}>
              {busy === 'appsumo_apply' ? 'Applying…' : 'Apply license'}
            </Button>
          </Stack>

          {account.appsumo?.licenseKey && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .8 }}>
              Current license: {account.appsumo.licenseKey} · Tier {account.appsumo.tier || '?'} · {account.appsumo.status || 'active'}
            </Typography>
          )}

          {appSumoLookup && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .8 }}>
              Verified: Tier {appSumoLookup.tier || '?'} · {appSumoLookup.status || 'unknown'}
              {appSumoLookup.ownerEmail ? ` · linked to ${appSumoLookup.ownerEmail}` : appSumoLookup.ownerUserId ? ' · already linked' : ' · not linked yet'}
            </Typography>
          )}

          {appSumoError && <Typography variant="caption" color="error" display="block" sx={{ mt: .8 }}>{appSumoError}</Typography>}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" gap={1.4} alignItems={{ lg: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography fontWeight={900}>Automation QA</Typography>
                <Chip
                  size="small"
                  label={testerEnabled ? 'Tester mode on' : 'Tester mode off'}
                  variant={testerEnabled ? 'filled' : 'outlined'}
                  sx={{ borderRadius: 1 }}
                />
                {account.status !== 'active' && <Chip size="small" label="Account inactive" color="warning" variant="outlined" sx={{ borderRadius: 1 }} />}
                {!account.mailboxes?.length && <Chip size="small" label="No mailbox" color="warning" variant="outlined" sx={{ borderRadius: 1 }} />}
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .45 }}>
                Login: {account.email || account.accountId}
                {account.mailboxes?.length > 0
                  ? ` · Connected: ${account.mailboxes.map((mailbox) => mailbox.email).filter(Boolean).join(', ')}`
                  : ' · No connected mailbox'}
              </Typography>

              {testerEnabled && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .2 }}>
                  Only emails sent after Tester mode was enabled are accelerated. Product days are simulated as minutes.
                </Typography>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={.8} alignItems={{ sm: 'center' }}>
              <Select
                size="small"
                value={minutesPerDay}
                onChange={(event) => setMinutesPerDay(event.target.value)}
                disabled={!testerEnabled || Boolean(busy)}
                sx={{ minWidth: 135 }}
              >
                {[1, 2, 3, 5].map((value) => (
                  <MenuItem key={value} value={String(value)}>{value} min / day</MenuItem>
                ))}
              </Select>
              {testerEnabled && (
                <Button size="small" variant="text" onClick={saveTesterSpeed} disabled={Boolean(busy)}>
                  Save timing
                </Button>
              )}
              <Button
                size="small"
                variant={testerEnabled ? 'contained' : 'outlined'}
                onClick={toggleTesterMode}
                disabled={Boolean(busy)}
              >
                {busy === 'tester_mode' ? 'Saving…' : testerEnabled ? 'Disable tester mode' : 'Enable tester mode'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={runTestNow}
                disabled={!testerEnabled || Boolean(busy)}
              >
                {busy === 'run_test' ? 'Running…' : 'Run test now'}
              </Button>
              <Button size="small" color="inherit" onClick={loadDiagnostics} disabled={Boolean(busy)}>
                {busy === 'diagnostics' ? 'Loading…' : 'Diagnostics'}
              </Button>
            </Stack>
          </Stack>

          <AutomationResult result={testResult} />

          {showDiagnostics && diagnostics && (
            <Box sx={{ mt: 1.2, p: 1.35, bgcolor: 'rgba(26,29,49,.025)', borderRadius: 1.2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  <strong>Threads:</strong> {diagnostics.threadCount || 0} · due {diagnostics.dueCount || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Usage doc:</strong> {diagnostics.usageDocId || '—'}
                </Typography>
                {diagnostics.account?.testerMode?.enabledAt && (
                  <Typography variant="body2">
                    <strong>Test mode since:</strong> {formatAdminDate(diagnostics.account.testerMode.enabledAt)}
                  </Typography>
                )}
              </Stack>

              {(diagnostics.mailboxes || []).map((mailbox) => {
                const lastScan = mailbox.lastScanSummary || diagnostics.usage?.lastScan || null;
                return (
                  <Typography key={mailbox.mailboxId} variant="caption" color="text.secondary" display="block" sx={{ mt: .55 }}>
                    {mailbox.email} · {mailbox.provider} · {mailbox.health || mailbox.status || 'unknown'}
                    {lastScan ? ` · last scan ${lastScan.scanned || 0} scanned / ${lastScan.found || 0} found / ${lastScan.saved || 0} saved` : ''}
                    {mailbox.lastScanError ? ` · ${mailbox.lastScanError}` : ''}
                  </Typography>
                );
              })}

              {(diagnostics.workspaces || []).map((workspace) => {
                const settings = workspace.settings || {};
                return (
                  <Typography key={workspace.workspaceId} variant="caption" color="text.secondary" display="block" sx={{ mt: .55 }}>
                    {workspace.name}: {settings.mode === 'send' ? 'Auto-send' : 'Draft mode'} · automatic drafts {settings.autoCreateDrafts ? 'ON' : 'OFF'} · cadence {settings.minDays || 3}–{settings.maxDays || 5} days
                    {settings.mode === 'send' ? ` · ${settings.autoSendPolicy || 'all'}` : ''}
                  </Typography>
                );
              })}

              {Object.keys(diagnostics.bySkippedReason || {}).length > 0 && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: .55 }}>
                  Blocks: {Object.entries(diagnostics.bySkippedReason).map(([reason, count]) => `${humanizeReason(reason)} (${count})`).join(' · ')}
                </Typography>
              )}

              <Button size="small" color="inherit" sx={{ mt: .6 }} onClick={() => setShowDiagnostics(false)}>Hide</Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ md: 'center' }}>
          <Select size="small" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 140 }}>
            {['active', 'inactive', 'paused', 'blocked'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </Select>
          <Button variant="outlined" onClick={saveStatus} disabled={busy === 'status'}>Update status</Button>
          <TextField size="small" type="number" label="Credits" value={credits} onChange={(event) => setCredits(event.target.value)} sx={{ width: 130 }} />
          <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addCredits} disabled={busy === 'credits'}>Adjust</Button>
        </Stack>

        {account.mailboxes?.length > 0 && (
          <Stack spacing={.8} mt={2}>
            {account.mailboxes.map((mailbox) => (
              <Stack key={mailbox.id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
                <Typography variant="body2" color="text.secondary" noWrap>{mailbox.email} · {mailbox.provider} · {mailbox.health || mailbox.status}</Typography>
                <Button size="small" color="inherit" startIcon={<RestartAltRoundedIcon />} onClick={() => resetMailbox(mailbox.id)} disabled={busy === mailbox.id}>Reset mailbox</Button>
              </Stack>
            ))}
          </Stack>
        )}

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteForeverRoundedIcon />}
          onClick={deleteAccount}
          disabled={Boolean(busy)}
        >
          Delete account
        </Button>
      </CardContent>
    </Card>
  );
}

export function AdminPage() {
  const { state } = useApp();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const canView = Boolean(state.admin?.isAdmin);

  const load = async () => {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      const result = await adminListAccounts({ search, limit: 50 });
      setAccounts(result.accounts || []);
    } catch (err) {
      setError(err?.message || 'Could not load admin accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canView]);

  const totals = useMemo(() => ({
    accounts: accounts.length,
    drafts: accounts.reduce((sum, item) => sum + Number(item.followUpsDrafted || 0), 0),
    found: accounts.reduce((sum, item) => sum + Number(item.opportunitiesFound || 0), 0),
  }), [accounts]);

  if (!canView) {
    return (
      <Box className="app-grid" minHeight="100vh">
        <AppHeader />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <NudgeAlert tone="error">You do not have Nudge admin access.</NudgeAlert>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="app-grid" minHeight="100vh" sx={{ '& .MuiButton-root': { borderRadius: 1, textTransform: 'none' } }}>
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Button color="inherit" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/home')}>Back</Button>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} alignItems={{ md: 'flex-end' }} mt={2}>
          <Box>
            <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 52 } }}>Nudge admin.</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>Manage testers, AppSumo tiers, bonus credits, and mailbox resets.</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Search email or license" value={search} onChange={(event) => setSearch(event.target.value)} />
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</Button>
          </Stack>
        </Stack>

        {message && <NudgeAlert tone="success" sx={{ mt: 2 }}>{message}</NudgeAlert>}
        {error && <NudgeAlert tone="error" sx={{ mt: 2 }}>{error}</NudgeAlert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={3}>
          <Card sx={{ flex: 1, borderRadius: 1.25 }}><CardContent><Typography variant="h3">{totals.accounts}</Typography><Typography color="text.secondary">Accounts loaded</Typography></CardContent></Card>
          <Card sx={{ flex: 1, borderRadius: 1.25 }}><CardContent><Typography variant="h3">{formatLimit(totals.found)}</Typography><Typography color="text.secondary">Opportunities found</Typography></CardContent></Card>
          <Card sx={{ flex: 1, borderRadius: 1.25 }}><CardContent><Typography variant="h3">{formatLimit(totals.drafts)}</Typography><Typography color="text.secondary">Drafts created</Typography></CardContent></Card>
        </Stack>

        <Stack spacing={1.6} mt={3}>
          {accounts.map((account) => (
            <AccountRow key={account.accountId} account={account} onChanged={async (msg) => { setMessage(msg); await load(); setTimeout(() => setMessage(''), 2600); }} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
